// @file Request Reset Password store
import { isEmail } from '@@/bits/email'
import { captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { getAndClearSearchParam } from '@@/bits/location'
import { User as UserApi } from '@@/dashboard/padlet_api'
import { HttpCode } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum RequestResetPasswordStatus {
  Initial = 'Initial',
  Updating = 'Updating',
  Errored = 'Errored',
  Completed = 'Completed',
}

export enum RequestResetPasswordStep {
  Submission = 'Submission',
  KnownEmail = 'KnownEmail',
  KnownEmailResent = 'KnownEmailResent',
  UnknownEmail = 'UnknownEmail',
}

export const useRequestResetPasswordStore = defineStore('requestResetPassword', () => {
  const globalSnackbarStore = useGlobalSnackbarStore()

  // State
  const email = ref('')
  const emailValidationMessage = ref('')
  const requestResetPasswordStatus = ref(RequestResetPasswordStatus.Initial)
  const requestResetPasswordStep = ref<RequestResetPasswordStep>(RequestResetPasswordStep.Submission)

  // Getters
  const isEmailValid = computed(() => emailValidationMessage.value.length === 0)

  // Actions
  const initializeState = (): void => {
    email.value = getAndClearSearchParam('email') || ''
  }

  const setEmail = (newEmail: string): void => {
    email.value = newEmail
  }

  const setEmailValidationMessage = (message: string): void => {
    emailValidationMessage.value = message
  }

  const setRequestResetPasswordStatus = (newRequestResetPasswordStatus: RequestResetPasswordStatus): void => {
    requestResetPasswordStatus.value = newRequestResetPasswordStatus
  }

  const requestResetPassword = async (): Promise<void> => {
    const trimmedEmail = email.value.trim()

    if (trimmedEmail === '') {
      setEmailValidationMessage(__('Email can not be blank'))
      return
    }

    if (!isEmail(trimmedEmail)) {
      setEmailValidationMessage(__('Email is invalid'))
      return
    }

    try {
      requestResetPasswordStatus.value = RequestResetPasswordStatus.Updating
      await UserApi.requestResetPassword({ email: email.value })
      requestResetPasswordStatus.value = RequestResetPasswordStatus.Completed
      requestResetPasswordStep.value = RequestResetPasswordStep.KnownEmail

      // TODO: handle native and non-native users.
    } catch (e) {
      if (e.status === HttpCode.NotFound) {
        requestResetPasswordStep.value = RequestResetPasswordStep.UnknownEmail
        requestResetPasswordStatus.value = RequestResetPasswordStatus.Initial
        return
      }
      requestResetPasswordStatus.value = RequestResetPasswordStatus.Errored
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'RequestResetPassword' })
    }
  }

  const sendRequestResetPasswordEmail = async (): Promise<void> => {
    try {
      await UserApi.requestResetPassword({ email: email.value })
    } catch (e) {
      /**
       * We don't want to handle errors in this case, since we assume
       * there won't be any related to the user input.
       * If this fails, it's due to a server error. We ignore.
       */

      captureFetchException(e, { source: 'RequestResetPasswordResendEmail' })
    }
  }

  return {
    // State
    email,
    emailValidationMessage,
    requestResetPasswordStatus,
    requestResetPasswordStep,

    // Getters
    isEmailValid,

    // Actions
    initializeState,
    setEmail,
    setEmailValidationMessage,
    setRequestResetPasswordStatus,
    requestResetPassword,
    sendRequestResetPasswordEmail,
  }
})
